import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { mergeLive, typingSays, typingSender, useConversationLive, useTypists } from '../api/live'
import { CannotSendThisFile, isVideo, MAX_PICTURES_PER_MESSAGE, refusalFor, uploadPicture } from '../api/attachments'
import type { Compression, CompressionOutcome } from '../api/videoCompress'
import { AddPictures, FailedUploads, offerFor, PictureGrid, PictureTray, SmallerCopyOffer, type PendingPicture } from './MessagePictures'
import { ArrowLeft, Bell, BellOff, Check, Copy, MoreHorizontal, Pencil, SendHorizontal, SmilePlus } from 'lucide-react'
import { fetchContent, fill, text } from '../api/content'
import {
  refreshConversations,
  conversationKey,
  editMessage,
  fetchConversation,
  fetchHistory,
  fetchMessages,
  hideMessage,
  insertMention,
  markRead,
  matchesReach,
  mentionQuery,
  mentionsIn,
  messagesKey,
  NotHereError,
  openDirect,
  refreshRoster,
  sendMessage,
  setMuted,
  setPinned,
  setReaction,
  whenLabel,
  type ConversationPerson,
  type Message,
  type MessagePage,
  type Reaction,
  type ReactionOffered,
} from '../api/conversations'
import {
  chipKey,
  COMPOSE_KEY,
  fetchCompose,
  offeredKinds,
  taskFrom,
} from '../api/conversations'
import { kindName } from '../api/asks'
import { addDays, today } from '../api/calendar'
import { fetchPortalDancer, fetchPortalHome, repairableOf } from '../api/portal'
import { navSubjectFrom } from '../navSubject'
import { useCan, useSession } from '../useSession'
import AssignTaskDialog from './AssignTaskDialog'
import DeclareOutDay, { refreshAfterDeclaring } from './DeclareOutDay'
import Modal from './Modal'
import { DancerPicker, MakeupOffer } from './PortalView'
import { AskForm } from './PrivateRequestsPanel'
import ReportAbsence from './ReportAbsence'

type Content = Parameters<typeof text>[0]

const DOT = ' · '

// The chip's words per door, and the card's per state, until Tonya words them (A-16).
const CHIP_WORDS: Record<string, string> = {
  cover: 'Ask somebody to cover',
  task: 'Make this a task',
  'private-lesson': 'Ask for a private lesson',
  makeup: 'Book a makeup',
  absence: 'Report an absence',
}

const CARD_WORDS: Record<string, string> = {
  open: 'Waiting for an answer',
  yes: 'Answered yes',
  no: 'Answered no',
  withdrawn: 'Taken back',
  countered: 'A different time was offered',
  unasked: 'Nobody has been asked yet',
  gone: 'No longer there',
}

// What a screen reader hears for a reaction when Tonya has not worded it yet.
const REACTION_WORDS: Record<string, string> = {
  thumbsUp: 'Thumbs up',
  heart: 'Heart',
  clap: 'Clap',
  laugh: 'Laugh',
  sad: 'Sad',
  party: 'Party',
}

/**
 * One conversation (M3, D-617, D-623).
 *
 * **Opening it marks it read, and that is what re-arms the bell.** The server
 * moves the marker and clears this conversation's notification rows in one
 * transaction; a parent who reads inside the screen and never opens the bell
 * would otherwise be silent for ever from that teacher. So the read is sent as
 * soon as the transcript has loaded, and the bell's query is invalidated
 * afterwards so the count on the rail agrees.
 *
 * **The household line** (D-617): a teacher talking to one guardian is told
 * the other exists, and whether she has spoken to him - the debt per-guardian
 * conversations owe.
 *
 * **The disclosure sits under the composer for every sender, every time**
 * (D-626). The studio can read this; nobody is told when it happens; and for
 * an account that is both staff and a parent, that being staff means the
 * studio's review covers her own family's conversations too. Finding that out
 * afterwards is how people stop trusting a tool.
 *
 * **Reactions, mentions, edits and the hide** (D-628, D-621). A reaction is
 * a count under the bubble and tells nobody; a mention is typed with `@`,
 * picked from the people in the conversation, and reaches somebody through
 * her mute; my own message can be edited and every version is kept behind
 * the *Edited* marker; and whoever may review can hide a message with a
 * reason, after which everybody else sees the row and none of the words.
 *
 * **A failed load is never an empty transcript** (D-209).
 */
function Conversation({ id }: { id: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { session } = useSession()
  const mayReview = useCan('ReviewConversations')

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  /*
   * <b>On the same cadence as the transcript, because the header now carries
   * something that can change under her</b> (D-692).
   *
   * It held only the title and the roster before, which do not move while a
   * screen is open - so a header fetched once was right. The pinned message is
   * not like that: somebody else can move it, and the studio can <i>hide</i>
   * it, which is the one remedy D-621 gives. Left at fetch-once, a hidden
   * message's words survived in the banner - the most prominent place on the
   * screen - while the transcript row beneath collapsed to *removed* within
   * fifteen seconds. The hide reaching every surface but the loudest one is
   * the failure worth paying a request a quarter-minute for.
   *
   * <b>And the banner is how the pin arrives at all</b> for the forty people
   * already sitting on the thread when it is pinned. Without this they see it
   * only after navigating away and back.
   */
  const header = useQuery({
    queryKey: conversationKey(id),
    queryFn: () => fetchConversation(id),
    enabled: id !== '',
    refetchInterval: 15_000,
  })

  const transcript = useInfiniteQuery({
    queryKey: messagesKey(id),
    queryFn: ({ pageParam }) => fetchMessages(id, pageParam),
    initialPageParam: undefined as string | undefined,
    // Older pages walk backwards from the oldest message loaded so far.
    getNextPageParam: (last) => (last.hasMore && last.messages.length > 0 ? last.messages[0].sentAt : undefined),
    enabled: id !== '',
    // The other side's replies appear without a reload. A push tap opens the
    // screen anyway; this is for the one already open.
    refetchInterval: 15_000,
  })

  /*
   * <b>Live, on top of the poll rather than instead of it</b> (D-750). New
   * messages arrive as they land; everything else the screen shows - a hide, a
   * pin, a reaction, a receipt - still comes on the fifteen-second polls above,
   * which are deliberately untouched so every failure here degrades to exactly
   * today.
   */
  const onArrive = useCallback(
    (messages: Message[]) =>
      queryClient.setQueryData<InfiniteData<MessagePage>>(messagesKey(id), (held) =>
        mergeLive(held, messages)),
    [queryClient, id],
  )

  // Too far behind to stream: one ordinary refetch rather than a stream
  // pretending to be the transcript.
  const onBehind = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: messagesKey(id) }),
    [queryClient, id],
  )

  const [typists, heardTyping] = useTypists()

  // One sender per conversation, so switching threads does not carry a
  // throttle across and swallow the first keystroke in the new one.
  const sayTyping = useMemo(() => typingSender(id), [id])

  useConversationLive(id, onArrive, onBehind, heardTyping)

  const read = useMutation({
    mutationFn: () => markRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      refreshConversations(queryClient)
    },
  })

  /*
   * Once per open, after the transcript exists - so the marker lands at or
   * after the newest message she can actually see, and the bell rows go with
   * it. Keyed on the newest message so a reply arriving while the screen is
   * open marks itself read too.
   */
  const pages = transcript.data?.pages ?? []
  const messages: Message[] = [...pages].reverse().flatMap((p) => p.messages)
  const newest = messages.length > 0 ? messages[messages.length - 1] : null
  const readUpTo = useRef<string | null>(null)

  useEffect(() => {
    if (!transcript.isSuccess || newest === null) return
    if (readUpTo.current === newest.id) return

    readUpTo.current = newest.id
    read.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript.isSuccess, newest?.id])

  const [draft, setDraft] = useState('')

  /*
   * <b>Pictures she has picked, uploading while she writes</b> (D-751). Each
   * one is reserved and sent the moment it is chosen, so pressing send is a
   * single ordinary post carrying ids - a photograph and a slow upload never
   * sit between her and the message.
   */
  const [pictures, setPictures] = useState<PendingPicture[]>([])
  const [caret, setCaret] = useState(0)
  const [lastError, setLastError] = useState<string | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)

  const people: ConversationPerson[] = header.data?.people ?? []
  const group = header.data?.kind === 'Group' ? header.data : null
  const creatorName = group?.people.find((p) => p.accountId === group.createdByAccountId)?.name ?? ''

  /*
   * Who may be named: everybody here, except in a private group, where a
   * member's reply lands in her own conversation with the creator (D-618) -
   * the server refuses any other name there, so the picker must not offer one.
   */
  const mentionable =
    group && group.replyMode === 'Private' && !group.isCreator
      ? people.filter((p) => p.accountId === group.createdByAccountId)
      : people

  /*
   * The asks chip (D-630): the doors this account may open, each with the
   * words that offer it, decided by the server from who she is. The draft is
   * read against them on every keystroke and nothing is created until she
   * taps one; "just a message" holds until the draft stops looking like a
   * request, rather than for one keystroke.
   */
  const compose = useQuery({ queryKey: COMPOSE_KEY, queryFn: fetchCompose, staleTime: 5 * 60_000, retry: false })
  const kinds = offeredKinds(draft, compose.data)
  const [dismissed, setDismissed] = useState(false)
  const [door, setDoor] = useState<string | null>(null)
  const [dancerId, setDancerId] = useState<string | null>(null)

  useEffect(() => {
    if (kinds.length === 0) setDismissed(false)
  }, [kinds.length])

  /*
   * A card whose send failed is retried by Send, never re-offered by the
   * chip: the thing exists on the envelope already, and a second tap would
   * make a second one. Held until her words go or are cleared.
   */
  const [pendingSubject, setPendingSubject] = useState<{ type: string; id: string } | null>(null)

  useEffect(() => {
    if (draft.trim().length === 0) setPendingSubject(null)
  }, [draft])

  /*
   * The three doors a parent opens for a dancer need to know which one. Her
   * household is asked only once a door is open; one dancer is chosen for
   * her, more than one is a question first.
   */
  const needsDancer = door === 'private-lesson' || door === 'makeup' || door === 'absence'
  const home = useQuery({
    queryKey: ['portal'],
    queryFn: fetchPortalHome,
    enabled: needsDancer,
  })
  const dancers = home.data?.dancers ?? []
  const chosenDancer = needsDancer ? (dancerId ?? (dancers.length === 1 ? dancers[0].dancerId : null)) : null
  const dancerView = useQuery({
    queryKey: ['portal-dancer', chosenDancer],
    queryFn: () => fetchPortalDancer(chosenDancer ?? ''),
    enabled: chosenDancer !== null,
  })

  const send = useMutation({
    /*
     * An ordinary message names people; a card names a thing (D-630). The
     * server refuses both at once, so a card carries no mentions.
     */
    mutationFn: ({
      body,
      subject,
      attachmentIds,
    }: {
      body: string
      subject?: { type: string; id: string } | null
      attachmentIds?: string[]
    }) =>
      sendMessage(
        id,
        body,
        subject ? [] : mentionsIn(body, mentionable),
        subject ?? null,
        attachmentIds ?? [],
      ),
    onSuccess: (posted) => {
      setDraft('')
      setCaret(0)
      /*
       * <b>Only the rows that actually went.</b> Clearing the whole tray would
       * take a photograph she picked while the post was in flight with it -
       * its preview revoked, its row gone, and its upload landing on nothing.
       * The object URLs of the ones that went are revoked here because each
       * pins its file in memory until something does.
       */
      const spent = new Set(posted.message.attachments?.map((picture) => picture.id) ?? [])

      setPictures((held) => {
        const gone = held.filter((p) => p.attachment && spent.has(p.attachment.id))
        gone.forEach((p) => URL.revokeObjectURL(p.previewUrl))

        return held.filter((p) => !gone.includes(p))
      })
      setLastError(null)
      setPendingSubject(null)
      void queryClient.invalidateQueries({ queryKey: messagesKey(id) })
      refreshConversations(queryClient)

      /*
       * A private reply to a group lands in the pair's own conversation
       * (D-618), so she is taken to where her words went rather than left
       * looking at a group that does not show them.
       */
      if (posted.landedIn && posted.landedIn !== id) {
        void queryClient.invalidateQueries({ queryKey: messagesKey(posted.landedIn) })
        navigate(`/messages/${posted.landedIn}`)
      }
    },
    onError: (e) => setLastError(e.message),
  })

  const mute = useMutation({
    mutationFn: (muted: boolean) => setMuted(id, muted),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKey(id) })
      refreshConversations(queryClient)
    },
  })

  const [refreshed, setRefreshed] = useState<number | null>(null)

  // Adds whoever the audience resolves to now; never removes anybody (D-618).
  const refresh = useMutation({
    mutationFn: () => refreshRoster(id),
    onSuccess: (result) => {
      setRefreshed(result.added)
      void queryClient.invalidateQueries({ queryKey: conversationKey(id) })
    },
  })

  const start = useMutation({
    mutationFn: openDirect,
    onSuccess: (view) => {
      refreshConversations(queryClient)
      navigate(`/messages/${view.id}`)
    },
  })

  /*
   * Move the pin, or take it off (D-692, item 34). The header is where the
   * pinned message lives, so that is what is re-read - the transcript has not
   * changed and re-fetching it would scroll her away from what she just did.
   *
   * <b>Not called `pin`</b>, because `pin(subject)` already exists in this file
   * and means something else entirely: attaching a request card to the message
   * being sent (D-630). Two meanings of one word in one file is how the wrong
   * one gets called.
   */
  const holdAtTop = useMutation({
    mutationFn: ({ messageId, on }: { messageId: string; on: boolean }) => setPinned(id, messageId, on),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKey(id) })
    },
  })

  // The newest message is what she came for; keep it in view as they arrive.
  const bottom = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // Guarded: jsdom has no scrollIntoView, and a transcript that cannot
    // scroll is still a transcript.
    if (typeof bottom.current?.scrollIntoView === 'function') bottom.current.scrollIntoView({ block: 'end' })
  }, [newest?.id])

  const ready = pictures.filter((picture) => picture.status === 'ready')

  /*
   * **Encoding holds Send exactly as uploading does** (D-912). A tile being
   * made smaller has no attachment id yet and is about to start a second
   * transfer, so sending during it would post the message without it — the
   * same loss this gate was built to stop, arriving through the new state.
   */
  const uploading = pictures.some(
    (picture) => picture.status === 'uploading' || picture.status === 'shrinking',
  )

  /*
   * <b>A picture with no caption is a whole message</b> (D-751), so the box
   * being empty is no longer a reason to refuse - but a picture still on its
   * way is: sending now would refuse the message server-side and lose her
   * words with it.
   */
  const somethingToSend = draft.trim().length > 0 || ready.length > 0

  function submit(e: FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!somethingToSend || uploading || send.isPending) return
    send.mutate({ body, subject: pendingSubject, attachmentIds: ready.map((picture) => picture.attachment!.id) })
  }

  /*
   * <b>Picked, uploaded, and shown as a thumbnail straight away.</b> The
   * server's ceiling is the one that counts; this one only stops her picking a
   * seventh and being refused at the end, after every upload had been paid for.
   */
  function addPictures(files: File[]) {
    const room = MAX_PICTURES_PER_MESSAGE - pictures.length
    if (room <= 0) return

    for (const file of files.slice(0, room)) {
      const key = `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`
      const previewUrl = URL.createObjectURL(file)

      /*
       * ⚠ <b>Videos and PDFs used to vanish here</b> (D-895). This read
       * <c>if (!file.type.startsWith('image/')) continue</c> — so the picker
       * offered video and PDF, the server accepted them, the bubble rendered
       * them, and this one line in the middle dropped them with no message.
       * Reported from QA: <i>"click add and then it doesn't actually do
       * anything... it just goes back to the chat page with nothing sitting
       * there ready to send."</i>
       *
       * <b>A refusal is shown, never skipped.</b> The row goes in as failed
       * with its reason, because a control that silently does nothing is worse
       * than one that says no — she cannot tell it apart from a control that
       * worked.
       */
      const refusal = refusalFor(file)

      if (refusal !== null) {
        setPictures((held) => [
          ...held,
          { key, name: file.name, previewUrl, contentType: file.type, status: 'failed', error: refusal },
        ])

        continue
      }

      setPictures((held) => [
        ...held,
        {
          key,
          name: file.name,
          previewUrl,
          contentType: file.type,
          status: 'uploading',
          /*
           * **Only a video can be offered a smaller copy** (D-912), and only on
           * its first attempt. The original is kept here because the offer is
           * answered minutes later, by which time the picker's `File` list is
           * long gone.
           */
          smaller: isVideo(file.type) ? { file } : undefined,
        },
      ])

      startUpload(key, file)
    }
  }

  /**
   * One attempt at putting a file where the message will point.
   *
   * ⚠ **Called a second time for the same tile when she asks for a smaller
   * copy** (D-912), and that is why every write checks the controller it owns
   * is still the tile's. The first attempt is aborted, and its rejection
   * arrives *after* the second has started — read as a failure it would mark a
   * running upload dead, with a sentence about a connection that is fine.
   */
  function startUpload(key: string, file: File, dimensions?: { width: number; height: number }) {
    // One per attempt, so taking this tile back off leaves the others alone.
    const stop = new AbortController()

    setPictures((held) =>
      held.map((picture) =>
        picture.key === key
          ? {
              ...picture,
              status: 'uploading' as const,
              stop,
              progress: undefined,
              error: undefined,
              // What a retry would re-send, which is this file and not the
              // original she picked (D-913).
              sending: { file, dimensions },
            }
          : picture))

    /** This attempt's own row, or nothing at all. */
    const mine = (held: PendingPicture[], write: (p: PendingPicture) => PendingPicture) =>
      held.map((picture) => (picture.key === key && picture.stop === stop ? write(picture) : picture))

    void uploadPicture(id, file, {
      signal: stop.signal,
      dimensions,
      onProgress: (fraction) =>
        setPictures((held) => mine(held, (picture) => ({ ...picture, progress: fraction }))),
      /*
       * **Her real speed, at most once, off real bytes storage confirmed.** It is
       * recorded rather than acted on: the offer needs the video's length too,
       * and which of the two lands first is not something this can know.
       */
      onRate: (measured) =>
        setPictures((held) =>
          mine(held, (picture) =>
            picture.smaller
              ? { ...picture, smaller: { ...picture.smaller, measured } }
              : picture)),
    })
      .then((attachment) =>
        setPictures((held) =>
          mine(held, (picture) => ({ ...picture, status: 'ready' as const, attachment }))))
      .catch((e: Error) =>
        setPictures((held) =>
          mine(held, (picture) => ({
            ...picture,
            status: 'failed' as const,
            error: e.message,
            // A file this browser cannot decode fails the same way for ever,
            // so it is not offered a retry (D-913).
            retryable: !(e instanceof CannotSendThisFile),
          }))))
  }

  /**
   * Send the same bytes again (D-913).
   *
   * **The file the failed attempt was carrying**, so a video she already waited
   * for the encode of does not go through it twice. A fresh reservation comes
   * with the new attempt — the old one is spent, since a destination takes
   * exactly one write (D-751).
   */
  function retry(key: string) {
    const picture = pictures.find((held) => held.key === key)
    const again = picture?.sending

    // No `sending` is a refusal rather than a failure, and the banner does not
    // offer a button for one; this guard is for the race, not the state.
    if (!picture || !again || picture.status !== 'failed') return

    startUpload(key, again.file, again.dimensions)
  }

  /** What the browser read off the preview, so the offer can count the encode. */
  function noteDuration(key: string, seconds: number) {
    setPictures((held) =>
      held.map((picture) =>
        picture.key === key && picture.smaller
          ? { ...picture, smaller: { ...picture.smaller, seconds } }
          : picture))
  }

  /** She read the offer and chose the full video. It is already uploading. */
  function keepFull(key: string) {
    setPictures((held) =>
      held.map((picture) =>
        picture.key === key && picture.smaller
          ? { ...picture, smaller: { ...picture.smaller, settled: true } }
          : picture))
  }

  /**
   * She chose the smaller copy: stop, encode, start again (D-903, D-912).
   *
   * ⚠ **The running transfer is stopped first, and that costs the blocks it
   * had already staged.** Bounded by how long she took to read the offer —
   * seconds on the connection this only ever appears on — and the alternative
   * is worse: an encode and an upload competing for the same weak uplink,
   * which slows the thing she asked for to make the thing she abandoned
   * finish.
   */
  async function shrink(key: string) {
    const picture = pictures.find((held) => held.key === key)
    const original = picture?.smaller?.file
    if (!picture || !original) return

    /*
     * ⚠ **Structural rather than timing-dependent.** React flushes discrete
     * events, so the banner almost certainly unmounts before a second tap can
     * land — but *almost* is the wrong guarantee for this one: a second call
     * would start a second encode, and because the tile is settled by then
     * `offerFor` returns null and `worthSeconds` goes undefined, which
     * `compressVideo` reads as **never give up**.
     */
    if (picture.smaller?.settled || picture.status !== 'uploading') return

    /*
     * **The deadline the banner's own arithmetic produced** (D-903). The
     * encoder gives up when it is costing more than the upload it saves, and
     * the saving it is judged against has to be the one she was shown — two
     * answers to that would be a quoted wait the encoder is not aiming at.
     */
    const worthSeconds = offerFor(picture)?.savedByShrinking

    picture.stop?.abort()

    const stop = new AbortController()

    setPictures((held) =>
      held.map((row) =>
        row.key === key && row.smaller
          ? {
              ...row,
              status: 'shrinking' as const,
              stop,
              progress: undefined,
              smaller: { ...row.smaller, settled: true },
            }
          : row))

    /*
     * **Loaded only when she asks for it.** `observed` 2026-10-19 off a real
     * build: mediabunny lands in its own 539 KB chunk, so nobody sending a
     * photograph — nor anybody on wifi, which is most of this studio — pays
     * for those bytes.
     *
     * ⚠ **And that chunk is the one thing here that can throw.** The SPA builds
     * with `emptyOutDir`, so a deploy replaces every fingerprinted file (D-520)
     * — a tab opened before one and tapping this after it asks for a chunk that
     * no longer exists. Unhandled, the tile sits *Making it smaller* for ever
     * with the X as the only way out. `compressVideo` itself never throws by
     * contract, which is exactly why it would have been easy to leave this
     * uncaught.
     */
    const made = await (async (): Promise<Compression> => {
      try {
        const { compressVideo } = await import('../api/videoCompress')

        return await compressVideo(original, {
          signal: stop.signal,
          worthSeconds,
          onProgress: (fraction) =>
            setPictures((held) =>
              held.map((row) =>
                row.key === key && row.stop === stop ? { ...row, progress: fraction } : row)),
        })
      } catch {
        // The same outcome by a different route, and it is the honest one: this
        // device could not do it, so the original goes and she is told.
        return { file: original, outcome: 'cannot here', width: 0, height: 0, wasBytes: original.size }
      }
    })()

    // She took it off, or left the conversation, while it encoded.
    if (stop.signal.aborted) return

    /*
     * **The outcome is a sentence only when it is not what she asked for.** A
     * copy that came out smaller needs no announcement — the wait getting
     * shorter is the announcement — but a phone that cannot encode has sent her
     * back to the original wait she was trying to avoid, and silence there is a
     * button that did nothing.
     */
    /*
     * ⚠ **One arm per outcome, and keyed rather than chained.** This was a
     * ternary that routed *cannot here* to its own words and swept the other
     * three into *"already about as small as it goes"* — which is **false** of
     * `not worth it`, the outcome for a phone whose encode was abandoned for
     * overrunning the upload it would have saved. That is the device this
     * feature is aimed at, and it was being told a made-up fact about its own
     * file. A record keyed on the type cannot acquire a fourth outcome without
     * the compiler asking what to say about it.
     */
    const said: Record<CompressionOutcome, string | undefined> = {
      // The wait getting shorter is the announcement.
      compressed: undefined,
      'cannot here': `This phone could not make ${picture.name} smaller, so the full video is going.`,
      'not worth it': `Making ${picture.name} smaller was taking longer than sending it, so the full video is going.`,
      'already lean': `${picture.name} was already about as small as it goes, so the full video is going.`,
      'no better': `Making ${picture.name} smaller did not help, so the full video is going.`,
    }

    setPictures((held) =>
      held.map((row) =>
        row.key === key && row.smaller
          ? { ...row, smaller: { ...row.smaller, said: said[made.outcome] } }
          : row))

    /*
     * **Dimensions only where they were measured.** The unchanged outcomes
     * report zeroes, and handing those on as fact is how a portrait clip came
     * to be described as landscape (D-903) — left out, the ordinary
     * pass-through runs and the bubble falls back to a square as it does today.
     */
    startUpload(
      key,
      made.file,
      made.width > 0 && made.height > 0 ? { width: made.width, height: made.height } : undefined,
    )
  }

  /*
   * <b>And on the way out</b> (D-751). Every other path revokes as it goes,
   * but leaving the screen - the bell, the back arrow, another conversation -
   * drops the state with nothing to clean up after it, and each preview pins
   * the whole original file for the life of the tab. Three photographs picked
   * and thought better of is tens of megabytes, repeatable.
   *
   * The ref is what lets the effect see the current list while running only on
   * unmount: depending on `pictures` would revoke a live preview on every pick.
   */
  const held = useRef<PendingPicture[]>([])
  held.current = pictures

  useEffect(
    () => () =>
      held.current.forEach((picture) => {
        URL.revokeObjectURL(picture.previewUrl)

        /*
         * <b>And stop the bytes on the way out too</b> (D-908). Leaving the
         * conversation drops the tray, so an upload still running can no
         * longer become a message **by construction** — it is the same waste
         * the X used to allow, with no control left to press. The back arrow,
         * the bell and another thread all land here, and the conversation is
         * keyed on its id so switching remounts.
         */
        picture.stop?.abort()
      }),
    [],
  )

  function removePicture(key: string) {
    setPictures((held) => {
      held.filter((picture) => picture.key === key)
        .forEach((picture) => {
          URL.revokeObjectURL(picture.previewUrl)

          /*
           * **And stop the bytes** (D-908). This revoked the thumbnail and
           * dropped the row while the upload carried on — waste on a
           * photograph, minutes of a parent's cellular data on a video she has
           * already taken back, and a cancel control that cancelled nothing.
           */
          picture.stop?.abort()
        })

      return held.filter((picture) => picture.key !== key)
    })
  }

  function closeDoor() {
    setDoor(null)
    setDancerId(null)
  }

  /*
   * The card: her words, pointing at the thing the door made (D-630). Sent
   * as the message she was writing, so the conversation shows one thing and
   * not a message beside a card saying the same. The thing exists on the
   * envelope whether or not this send succeeds; a failure leaves her words
   * in the box and the error under it, like any send.
   */
  function pin(subject: { type: string; id: string }) {
    const body = draft.trim()
    closeDoor()
    setPendingSubject(subject)
    if (body.length === 0 || send.isPending) return
    send.mutate({ body, subject })
  }

  /*
   * The mention picker: open while an `@` is being typed, offering the people
   * in this conversation whose name matches what follows it. Picking one
   * writes `@Name ` into the draft; the ids are read back off the words at
   * send time (`mentionsIn`), so there is no second list to fall out of step.
   */
  const mention = mentionQuery(draft, caret)

  // A whole name, in any case, is a finished mention: the picker closes and
  // Enter sends, rather than offering the next longer name (Sam, Samantha).
  const finished = mention
    ? mentionable.some((p) => p.name.trim().toLowerCase() === mention.query.trim().toLowerCase())
    : false
  const offered = mention && !finished ? mentionable.filter((p) => matchesReach(p, mention.query)) : []

  function pick(person: ConversationPerson) {
    if (!mention) return
    const next = insertMention(draft, mention.start, caret, person.name)
    setDraft(next.draft)
    setCaret(next.caret)
    const el = box.current
    if (el) {
      el.focus()
      // After React has written the new value, or the caret lands on the old one.
      // Guarded: jsdom has no frame to wait for, and the caret is a nicety.
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => el.setSelectionRange(next.caret, next.caret))
      }
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      // Enter with the picker open takes the first person rather than sending.
      if (offered.length > 0) pick(offered[0])
      else submit(e)
    }
    if (e.key === 'Escape' && mention) {
      // Closes the picker by moving the caret off the query; the words stay.
      setCaret(0)
    }
  }

  const bothStaffAndParent = session ? navSubjectFrom(session).isStaff && navSubjectFrom(session).isParent : false

  const now = new Date()

  const reactionsOffered = header.data?.reactionsOffered ?? []

  if (header.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink content={content} />
        <div className="rounded-2xl border border-line bg-surface px-5 py-6" role="alert">
          <p className="text-[13px] leading-relaxed text-ink-2">
            {header.error instanceof NotHereError
              ? text(content, 'messages.notHere', 'That conversation is not here.')
              : text(content, 'messages.failed', 'Your messages could not be loaded. Try again in a moment.')}
          </p>
        </div>
      </div>
    )
  }

  return (
    /*
      <b>A bounded column on a laptop, so the transcript scrolls inside it</b>
      (D-838). `asked`, 2026-09-28, on a screenshot: *"as i typed more messages, it
      continued to grow the size of the chat window, instead of creating an in
      window scroll... i feel like it should prob be a fixed height."*

      <b>`max-h`, not `h`</b>: a short conversation stays its own size rather than
      becoming a tall empty box, and a long one stops at the cap and scrolls inside.
      <b>The 11rem is the chrome above it</b> — measured at 147px, not guessed — so
      the composer lands inside the viewport instead of 61px under it, which is what
      a first attempt sized from the whole viewport did.

      <b>The same treatment the conversation list beside it already had</b> —
      `md:max-h-[calc(100dvh-2rem)] md:overflow-y-auto` — which is why one pane
      behaved and the other did not.

      <b>Only from `md` up, and that is deliberate.</b> On a phone the detail takes
      the whole screen and the page itself is the scroller, which is how every
      messaging app on a phone behaves; pinning a height there would fight the
      browser's own chrome, which changes size as it hides and shows.
    */
    <div className="flex flex-col gap-3 md:max-h-[calc(100dvh-11rem)] md:min-h-0">
      {/*
        <b>The wrapper hides too, not just the link inside it</b> (D-702).
        `display:none` on the child leaves the parent a zero-height flex item,
        and the column's `gap-3` still fires on both sides of it — about 12px
        of dead space above the title at every width where the back link is
        gone. Named by an audit.
      */}
      <div className="flex items-start gap-3 md:hidden">
        <BackLink content={content} />
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl wrap-anywhere">
            {header.data?.title ?? text(content, 'messages.loading', 'Loading…')}
          </h2>
          {header.data && header.data.kind === 'Group' && (
            <p className="text-[12px] text-ink-2">
              {fill(content, 'messages.group.people', { count: header.data.people.length + 1 }) ||
                `${header.data.people.length + 1} people`}
              {DOT}
              {header.data.replyMode === 'Private'
                ? text(content, 'messages.group.private', 'Replies go to the sender only')
                : text(content, 'messages.group.shared', 'Replies go to everybody')}
            </p>
          )}
        </div>

        {header.data && (
          <button
            type="button"
            onClick={() => mute.mutate(!header.data.isMuted)}
            disabled={mute.isPending}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-lg border border-line bg-surface px-3 text-[13px] font-semibold text-ink-2"
            aria-pressed={header.data.isMuted}
          >
            {header.data.isMuted ? <Bell size={16} aria-hidden="true" /> : <BellOff size={16} aria-hidden="true" />}
            {header.data.isMuted
              ? text(content, 'messages.unmute', 'Unmute')
              : text(content, 'messages.mute', 'Mute')}
          </button>
        )}
      </div>

      {header.data && header.data.wouldAdd !== null && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-2">
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line bg-surface px-3 text-[13px] font-semibold text-ink-2 disabled:opacity-60"
          >
            {text(content, 'messages.group.refresh', 'Refresh the roster')}
            {DOT}
            {fill(content, 'messages.group.refresh.would', { count: header.data.wouldAdd }) ||
              `${header.data.wouldAdd} to add`}
          </button>
          {refreshed !== null && (
            <span role="status">
              {fill(content, 'messages.group.refresh.added', { count: refreshed }) || `Added ${refreshed}`}
            </span>
          )}
          {refresh.isError && (
            <span className="text-crit" role="alert">
              {refresh.error.message}
            </span>
          )}
        </div>
      )}

      {header.data?.household.map((other) => (
        <div
          key={other.accountId}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-surface-2 px-4 py-2 text-[12px] text-ink-2"
        >
          <span>
            {other.conversationId
              ? fill(content, 'messages.household.spoken', { name: other.name }) ||
                `${other.name} is also on this household. You have a conversation with them too.`
              : fill(content, 'messages.household.unspoken', { name: other.name }) ||
                `${other.name} is also on this household. You have not spoken to them.`}
          </span>
          {other.conversationId ? (
            <Link to={`/messages/${other.conversationId}`} className="font-semibold text-accent-2">
              {text(content, 'messages.household.open', 'Open')}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => start.mutate(other.accountId)}
              disabled={start.isPending}
              className="min-h-[24px] font-semibold text-accent-2"
            >
              {fill(content, 'messages.household.start', { name: other.name }) || `Message ${other.name}`}
            </button>
          )}
        </div>
      ))}

      {/*
        <b>The one message held at the top</b> (D-692, item 34).

        `asked`: *"in messaging, pin a response to the top of the chat."* Tonya
        asks forty parents something, one reply is the answer, and that answer
        stops being findable the moment the thread moves. So this sits outside
        the scrolling transcript rather than inside it.

        <b>It carries the whole message</b>, because the thread may not have
        scrolled back far enough to hold it — and the server has already applied
        every reason it should not show: hidden since (D-621), or said before
        this reader joined the group (D-617). Nothing to re-check here.
      */}
      {header.data?.pinned && (
        <section
          aria-label="Pinned message"
          className="flex flex-col gap-1 rounded-2xl border border-accent-2/40 bg-surface-2 px-4 py-3"
        >
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">Pinned</h2>
            {header.data.mayPin && (
              <button
                type="button"
                onClick={() => holdAtTop.mutate({ messageId: header.data!.pinned!.id, on: false })}
                disabled={holdAtTop.isPending}
                className="min-h-[24px] px-1 text-[12px] font-semibold text-accent-2"
              >
                Unpin
              </button>
            )}
          </div>
          {header.data.pinned.body && (
            <p className="text-[13px] wrap-anywhere text-ink">{header.data.pinned.body}</p>
          )}
          {/*
            A pinned photograph with no caption would otherwise be a blank
            strip with a name on it (D-751). Showing the picture beats
            describing it, and it is the same grid the bubble draws.
          */}
          <PictureGrid conversationId={id} pictures={header.data.pinned.attachments ?? []} />
          <p className="text-[11.5px] text-ink-3">{header.data.pinned.authorName}</p>
        </section>
      )}

      {holdAtTop.isError && (
        <p className="text-[12px] text-crit" role="alert">
          {holdAtTop.error instanceof Error ? holdAtTop.error.message : 'That could not be pinned.'}
        </p>
      )}

      {/*
        <b>`min-h-0` is the half that does the work.</b> A flex child's minimum is
        its content, so `grow overflow-y-auto` alone still grows the column and
        scrolls nothing — the trap next to the `mx-auto max-w-[…]` one this project
        already has a rule about.
      */}
      <div className="flex grow flex-col gap-3 rounded-2xl border border-line bg-surface px-4 py-4 md:min-h-0 md:overflow-y-auto">
        {transcript.isPending && (
          <p className="text-[13px] text-ink-2" role="status">
            {text(content, 'messages.loading', 'Loading…')}
          </p>
        )}

        {transcript.isError && (
          <p className="text-[13px] text-crit" role="alert">
            {text(content, 'messages.failed', 'Your messages could not be loaded. Try again in a moment.')}
          </p>
        )}

        {transcript.hasNextPage && (
          <button
            type="button"
            onClick={() => void transcript.fetchNextPage()}
            disabled={transcript.isFetchingNextPage}
            className="self-center rounded-full border border-line px-3 py-1 text-[12px] font-semibold text-ink-2"
          >
            {text(content, 'messages.older', 'Show earlier messages')}
          </button>
        )}

        {transcript.isSuccess && messages.length === 0 && (
          <p className="text-[13px] text-ink-2">
            {text(content, 'messages.nothingYet', 'Nothing said yet.')}
          </p>
        )}

        <ol className="flex flex-col gap-4" aria-live="polite">
          {messages.map((m) => (
            <MessageRow
              key={m.id}
              conversationId={id}
              message={m}
              content={content}
              now={now}
              offered={reactionsOffered}
              mayReview={mayReview}
              mayPin={header.data?.mayPin ?? false}
              isPinned={header.data?.pinned?.id === m.id}
              onPin={(on) => holdAtTop.mutate({ messageId: m.id, on })}
              isGroup={header.data?.kind === 'Group'}
            />
          ))}
        </ol>
        <div ref={bottom} />
      </div>

      <form onSubmit={submit} className="flex flex-col gap-2">
        {mention && offered.length > 0 && (
          <ul
            role="listbox"
            aria-label={text(content, 'messages.mention.pick', 'Mention somebody')}
            className="flex max-h-40 flex-col overflow-y-auto rounded-lg border border-line bg-surface py-1"
          >
            {offered.map((p, i) => (
              <li key={p.accountId} role="option" aria-selected={i === 0}>
                <button
                  type="button"
                  onClick={() => pick(p)}
                  className="flex min-h-[44px] w-full items-center px-3 text-left text-[13.5px] hover:bg-surface-2"
                >
                  {p.name}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/*
          <b>Announced, not just drawn</b> (D-750 §9). A screen reader should
          hear that somebody is writing, and `polite` is the setting that waits
          for a pause rather than interrupting whatever is being read.

          <b>Always mounted, empty when nobody is typing.</b> A live region
          announces changes *inside* itself; one that appears along with its
          text is a new node, and most screen readers say nothing at all.
        */}
        <p className="px-1 text-[12px] italic text-ink-3" aria-live="polite">
          {typingSays(typists)}
        </p>

        <PictureTray
          pictures={pictures}
          onRemove={removePicture}
          onDuration={noteDuration}
          label={text(content, 'messages.pictures.tray', 'Pictures to send')}
        />

        <SmallerCopyOffer pictures={pictures} onShrink={shrink} onKeep={keepFull} />

        <FailedUploads pictures={pictures} onRetry={retry} />

        <div className="flex items-end gap-2">
          <AddPictures
            onFiles={addPictures}
            disabled={!header.isSuccess}
            full={pictures.length >= MAX_PICTURES_PER_MESSAGE}
            label={text(content, 'messages.pictures.add', 'Add a photo')}
          />
          <textarea
            ref={box}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value)
              setCaret(e.target.selectionStart ?? e.target.value.length)

              // Only while there is something to say: clearing the box is not
              // typing, and neither is tabbing through it.
              if (e.target.value.trim().length > 0) sayTyping()
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
            /*
             * Pasting a screenshot is how somebody on a laptop sends one, and
             * it is the only route that does not involve saving a file first.
             */
            onPaste={(e) => {
              const files = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'))
              if (files.length === 0) return

              e.preventDefault()
              addPictures(files)
            }}
            onKeyDown={onKey}
            rows={2}
            maxLength={4000}
            placeholder={text(content, 'messages.compose.placeholder', 'Write a message')}
            aria-label={text(content, 'messages.compose.placeholder', 'Write a message')}
            className="min-h-[44px] grow resize-y rounded-lg border border-line bg-surface px-3 py-2 text-[14px]"
          />
          <button
            type="submit"
            disabled={send.isPending || uploading || !somethingToSend || !header.isSuccess}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-on-accent disabled:opacity-50"
            aria-label={text(content, 'messages.compose.send', 'Send')}
          >
            <SendHorizontal size={18} aria-hidden="true" />
          </button>
        </div>

        {/*
          * The chip (D-630): an offer, one per door the draft looks like it
          * wants, and a way to say it is just a message. Tapping opens the
          * existing raise flow with her words carried in; nothing is created
          * by the words themselves.
          */}
        {/*
          * <b>Not offered while she has a picture attached</b> (D-751). A card
          * carries no pictures - the server refuses the pair - so offering the
          * door here would turn a photograph she has already uploaded into a
          * refusal at the end. The chips come back the moment the tray is
          * empty.
          */}
        {kinds.length > 0 && !dismissed && pendingSubject === null && pictures.length === 0 && (
          <div
            role="group"
            aria-label={text(content, 'messages.chip.label', 'Make this a request?')}
            className="flex flex-wrap items-center gap-2"
          >
            {kinds.map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setDoor(kind)}
                className="min-h-[32px] rounded-full border border-accent bg-accent-soft px-3 text-[12.5px] font-semibold text-accent-2"
              >
                {text(content, `messages.chip.${chipKey(kind)}`, CHIP_WORDS[kind] ?? kind)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="min-h-[32px] px-2 text-[12px] font-semibold text-ink-3 underline underline-offset-2"
            >
              {text(content, 'messages.chip.dismiss', 'Just a message')}
            </button>
            <span className="basis-full text-[11.5px] leading-relaxed text-ink-3">
              {text(content, 'messages.chip.hint', 'Nothing is created unless you tap one. Your words go with it.')}
            </span>
          </div>
        )}

        {door !== null && (home.isError || dancerView.isError) && (
          <p className="flex flex-wrap items-center gap-2 text-[12px] text-crit" role="alert">
            {text(content, 'messages.chip.failed', 'That could not be opened. Try again in a moment.')}
            <button type="button" onClick={closeDoor} className="font-semibold underline underline-offset-2">
              {text(content, 'messages.chip.close', 'Close')}
            </button>
          </p>
        )}

        {lastError && (
          <p className="text-[12px] text-crit" role="alert">
            {lastError}
          </p>
        )}

        {/*
          * A parent typing into a private group must know her answer goes to
          * one person, and the creator that hers goes to everybody (D-618).
          */}
        {group && group.replyMode === 'Private' && (
          <p className="text-[12px] font-semibold text-ink-2">
            {group.isCreator
              ? text(content, 'messages.group.creatorReply', 'Your reply goes to everybody in the group.')
              : fill(content, 'messages.group.replyPrivate', { name: creatorName }) ||
                `Your reply goes to ${creatorName} only.`}
          </p>
        )}

        <p className="text-[11.5px] leading-relaxed text-ink-3">
          {text(content, 'messages.mention.hint', 'Type @ to name somebody. They are told even if they have muted this conversation.')}
        </p>

        <p className="text-[11.5px] leading-relaxed text-ink-3">
          {text(content, 'messages.disclosure', 'The studio can review conversations. Nobody is told when it happens.')}
          {/* The staff clause follows the studio sentence it refers to; the parents sentence comes after, or 'that' points at the wrong thing. */}
          {bothStaffAndParent && (
            <>
              {' '}
              {text(content, 'messages.disclosure.staff', "As a member of staff, that includes your own family's conversations.")}
            </>
          )}
          {' '}
          {text(content, 'messages.disclosure.parents', "Parents can read their own dancers' conversations.")}
        </p>
      </form>

      {/*
        * The doors (D-630). Each is the existing raise flow, opened with her
        * words carried in; on confirm the flow hands back what it made and
        * the card is sent pointing at it. A cover pins the absence, which
        * follows whoever is asked now; a task and a private lesson pin the
        * ask; a makeup pins the teacher's question when the class was above
        * her level and nothing otherwise; an absence is not an ask, so her
        * words stay in the box for her to send.
        */}
      {door === 'cover' && (
        <DeclareOutDay
          onClose={closeDoor}
          /*
           * This dropped nothing at all, which was the third and worst copy of
           * the list (D-683). The per-row controls call `onDone` without the
           * bulk submit's wholesale invalidate, so asking somebody from inside
           * a conversation left her register, her home and the cover list a
           * version behind. Pre-existing; fixed here because the shared helper
           * made leaving it the odd choice.
           */
          onDone={() => refreshAfterDeclaring(queryClient)}
          initialNote={draft}
          onRaised={(sessions) => {
            const declared = sessions.find((s) => s.absenceId !== null)
            if (declared?.absenceId) pin({ type: 'Absence', id: declared.absenceId })
          }}
        />
      )}

      {door === 'task' && (
        <AssignTaskDialog
          onClose={closeDoor}
          suggested={taskFrom(draft)}
          onRaised={(askId) => pin({ type: 'Ask', id: askId })}
        />
      )}

      {door === 'absence' && chosenDancer !== null && home.data && (
        <ReportAbsence
          dancers={dancers}
          // The one she picked; the form still lets her tick a second child.
          preselected={[chosenDancer]}
          initialNote={draft}
          onClose={closeDoor}
        />
      )}

      {needsDancer && chosenDancer === null && home.data && (
        <Modal
          onDismiss={closeDoor}
          label={text(content, 'messages.chip.whichDancer', 'Which dancer?')}
          panelClassName="w-full max-w-[24rem] rounded-2xl border border-line bg-surface p-5"
        >
          <p className="mb-3 text-[14px] font-semibold">
            {text(content, 'messages.chip.whichDancer', 'Which dancer?')}
          </p>
          {/* The portal's own picker (A-17): a second list of her children would drift from it. */}
          <DancerPicker dancers={dancers} value={null} onChange={setDancerId} includeEveryone={false} />
        </Modal>
      )}

      {door === 'private-lesson' && chosenDancer !== null && dancerView.data && (
        <AskForm
          open
          dancerId={chosenDancer}
          dancerName={dancerView.data.name}
          paysFromCredit={dancerView.data.paysFromCredit}
          repairable={repairableOf(dancerView.data.missed)}
          initialNote={draft}
          onClose={closeDoor}
          onRaised={(askId) => pin({ type: 'Ask', id: askId })}
        />
      )}

      {door === 'makeup' && chosenDancer !== null && dancerView.data && (
        <Modal
          onDismiss={closeDoor}
          labelledBy="messages-makeup-heading"
          panelClassName="max-h-[90vh] w-full max-w-[32rem] overflow-y-auto rounded-2xl border border-line bg-surface p-5"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <h2 id="messages-makeup-heading" className="font-display text-lg wrap-anywhere">
              {fill(content, 'messages.chip.makeup.heading', { name: dancerView.data.name }) ||
                `Makeup classes for ${dancerView.data.name}`}
            </h2>
            <button
              type="button"
              onClick={closeDoor}
              className="shrink-0 py-1 text-[12.5px] font-semibold text-ink-3 underline underline-offset-2"
            >
              {text(content, 'messages.chip.close', 'Close')}
            </button>
          </div>
          <MakeupOffer
            dancer={dancerView.data}
            standing={dancerView.data.standing}
            embedded
            heading={null}
            nothingFits={text(
              content,
              'messages.chip.makeup.nothing',
              'Nothing in the next four weeks fits. Her schedule on the portal offers other weeks.',
            )}
            /*
              The next four weeks: there is no week control in here, so a
              window she cannot move has to be wide enough to hold something.
            */
            from={today()}
            to={addDays(today(), 28)}
            onBooked={(askId) => {
              if (askId) pin({ type: 'Ask', id: askId })
              else closeDoor()
            }}
          />
        </Modal>
      )}
    </div>
  )
}

/**
 * A card (D-630): her words, what they were made into, and how that stands
 * now - read live on every read, never written to the conversation. The
 * link to Requests is offered to staff only; a parent's request lives on
 * her portal, and a link that refuses the person holding it is worse than
 * none.
 */
function Card({
  message: m,
  content,
  readOnly = false,
}: {
  message: Message
  content: Content
  /** The supervisory read (D-626): the reviewer's own list would not hold this request, so no link. */
  readOnly?: boolean
}) {
  const { session } = useSession()
  const subject = m.subject
  if (!subject) return <span className="whitespace-pre-wrap">{m.body}</span>

  const staff = !readOnly && (session ? navSubjectFrom(session).isStaff : false)

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-bold tracking-[0.08em] text-ink-3 uppercase">
        {kindName(subject.kind, content)}
      </span>
      <span className="whitespace-pre-wrap">{m.body}</span>
      <span className="text-[12px] font-semibold text-ink-2">
        {text(content, `messages.card.${subject.state}`, CARD_WORDS[subject.state] ?? subject.state)}
        {staff && (
          <>
            {' '}
            {/*
              <b>A box, so the padding counts</b> (D-704). Inline in 12px text
              this measured <b>16px</b> tall at every width - under the 24px the
              layout audit sets - and it is the one thing on the card a teacher
              reaches for. Vertical padding on an inline element paints but
              takes no height, which is why it has to be `inline-block`; the
              same fix the family's payment link needed.
            */}
            <Link to="/asks" className="inline-block py-[5px] underline underline-offset-2">
              {text(content, 'messages.card.link', 'Open on Requests')}
            </Link>
          </>
        )}
      </span>
    </div>
  )
}

/**
 * One message: the bubble, its reactions, and whatever the reader may do to
 * it. Edit is the author's alone and never on a hidden message; Hide is the
 * reviewer's; React is anybody's, on anything that is not hidden or a system
 * line. A hidden message shows its words to a reviewer only, with the reason
 * under them (D-621).
 */
export function MessageRow({
  conversationId,
  message: m,
  content,
  now,
  offered,
  mayReview,
  readOnly = false,
  mayPin = false,
  isPinned = false,
  onPin,
  isGroup = false,
  pictureUrl,
}: {
  conversationId: string
  message: Message
  content: Content
  now: Date
  offered: ReactionOffered[]
  mayReview: boolean
  /** The supervisory view (D-626): the bubble and its counts, and nothing the reader may do. */
  readOnly?: boolean
  /** Whether this reader may move the pin at all (D-692) - the server's answer, not this screen's. */
  mayPin?: boolean
  /** Whether this row is the one currently held at the top. */
  isPinned?: boolean
  /** Moves the pin here, or takes it off. Absent where the parent offers none. */
  onPin?: (on: boolean) => void
  /** Whether this is a group, which decides how a read receipt reads (D-693). */
  isGroup?: boolean
  /**
   * Which door the pictures are read through (D-751).
   *
   * **Decided by the screen, because only the screen knows who is reading.**
   * A participant reads them behind her seat; the studio's supervisory view has
   * no seat and reads them behind `ReviewConversations`, audited. Left to the
   * component, the review screen would draw links that refuse the one person it
   * exists for.
   */
  pictureUrl?: (conversationId: string, attachmentId: string) => string
}) {
  const queryClient = useQueryClient()

  const [picking, setPicking] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [hiding, setHiding] = useState<string | null>(null)
  const [hideRefusal, setHideRefusal] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)

  /*
   * <b>The row's actions, out of the way until wanted</b> (D-715). `asked`: "in
   * say like teams you hover over it with a mouse to see the reactions, the edit
   * pencil, or the 3 dots to do things like pin etc. this would keep each line a
   * lot more clean". They were three words under every message she sent.
   *
   * Shown on hover, on keyboard focus inside the row, and - because a phone has
   * no hover - on a tap of the bubble, which is what `selected` holds. `more` is
   * the "..." menu, for the actions a row rarely needs.
   */
  const [selected, setSelected] = useState(false)
  const [more, setMore] = useState(false)
  const moreRef = useRef<HTMLDivElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)

  /*
   * <b>Press and hold opens the actions on a phone</b> (D-715, amended). `asked`:
   * <i>"the reaction and pin and edit on phone don't seem to work when tap and
   * hold"</i>. A hold is how a phone asks for a message's options, and it was
   * doing the phone's own thing instead - selecting text, or the long-press
   * menu - so nothing opened. Held half a second without moving, the toolbar
   * opens and the tap that ends the hold does not close it again. A quick tap
   * still toggles it, as before.
   */
  const pressTimer = useRef<number | null>(null)
  const pressFrom = useRef<{ x: number; y: number } | null>(null)
  const longPressed = useRef(false)
  const touching = useRef(false)

  /*
   * <b>Copy, because a hold no longer selects text</b> (D-715, from the audit). On a
   * touch screen the bubble is unselectable so a hold can open the actions, which
   * took away the only way to copy an address or a code out of a message. Shown
   * for a plain message with words; a card's words are not its content.
   */
  const [copied, setCopied] = useState(false)
  const copyable = !m.isHidden && !m.isSystem && m.subject === null && !!m.body
  const cancelPress = () => {
    if (pressTimer.current !== null) window.clearTimeout(pressTimer.current)
    pressTimer.current = null
    pressFrom.current = null
  }

  useEffect(() => () => cancelPress(), [])

  // The "..." list closes the way any popover does: Escape, which hands focus
  // back to the button that opened it, or a press anywhere else - which is
  // also what closes it when another row's "..." is opened.
  useEffect(() => {
    if (!more) return
    const onPointer = (e: PointerEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMore(false)
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      // Only her Escape: in the composer it belongs to the mention picker, and
      // taking focus from there would throw her back up into the transcript.
      // Nothing focused counts as hers: Safari does not focus a clicked button.
      const at = document.activeElement
      if (e.key !== 'Escape') return
      if (at && at !== document.body && !moreRef.current?.contains(at)) return
      setMore(false)
      moreButtonRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [more])

  const refresh = () => queryClient.invalidateQueries({ queryKey: messagesKey(conversationId) })

  const react = useMutation({
    mutationFn: ({ emoji, on }: { emoji: string; on: boolean }) => setReaction(conversationId, m.id, emoji, on),
    onSuccess: () => {
      setPicking(false)
      void refresh()
    },
  })

  const edit = useMutation({
    mutationFn: (body: string) => editMessage(conversationId, m.id, body),
    onSuccess: () => {
      setEditing(null)
      void refresh()
    },
  })

  const hide = useMutation({
    mutationFn: (reason: string) => hideMessage(conversationId, m.id, reason),
    onSuccess: () => {
      setHiding(null)
      setHideRefusal(null)
      void refresh()
    },
  })

  const history = useQuery({
    queryKey: [...messagesKey(conversationId), m.id, 'history'],
    queryFn: () => fetchHistory(conversationId, m.id),
    enabled: showHistory,
  })

  const glyphOf = (key: string) => offered.find((o) => o.key === key)?.glyph ?? key
  // A word for a screen reader, hers to edit (A-16); the key only as a last resort.
  const nameOf = (key: string) => text(content, `messages.reaction.${key}`, REACTION_WORDS[key] ?? key)
  const shown: Reaction[] = m.reactions.filter((r) => r.count > 0)

  // The reviewer's view of a hidden message: the words came, and so did the reason.
  const reviewerSees = m.isHidden && m.hiddenReason !== null
  const reactable = !readOnly && !m.isHidden && !m.isSystem
  const editable = !readOnly && m.isMine && !m.isHidden && !m.isSystem
  // A card is a system line only in that it cannot be edited: its words are a
  // person's, so the studio's one remedy (D-621) has to reach it.
  const hideable = !readOnly && mayReview && !m.isHidden && (!m.isSystem || m.subject !== null)
  // The same three conditions the pin route checks, so the control never
  // appears on a message the server would refuse (D-692).
  const pinnable = !readOnly && mayPin && onPin !== undefined && !m.isHidden && !m.isSystem

  function saveEdit(e: FormEvent) {
    e.preventDefault()
    const body = (editing ?? '').trim()
    if (body.length === 0 || edit.isPending) return
    edit.mutate(body)
  }

  function confirmHide(e: FormEvent) {
    e.preventDefault()
    const reason = (hiding ?? '').trim()
    if (reason.length === 0) {
      setHideRefusal(text(content, 'messages.hide.needsReason', 'Say why. The reason is kept with the message.'))
      return
    }
    if (hide.isPending) return
    hide.mutate(reason)
  }

  const hasActions = reactable || copyable || (editable && editing === null) || (hideable && hiding === null) || (pinnable && !isPinned)
  const toolbarShown = selected || picking || more

  return (
    <li
      className={`group flex flex-col gap-1 ${m.isMine ? 'items-end' : 'items-start'} ${
        hasActions && editing === null ? '[@media(pointer:coarse)]:select-none' : ''
      }`}
    >
      <div className="relative max-w-[85%]">
      <div
        onPointerDown={(e) => {
          // A second finger is a pinch, not a hold, and must not leave the first's timer running.
          cancelPress()
          if (!e.isPrimary) return
          touching.current = e.pointerType !== 'mouse'
          if (!touching.current || !hasActions || editing !== null) return

          longPressed.current = false
          pressFrom.current = { x: e.clientX, y: e.clientY }
          pressTimer.current = window.setTimeout(() => {
            longPressed.current = true
            pressTimer.current = null
            setSelected(true)
          }, 450)
        }}
        onPointerMove={(e) => {
          // A scroll is not a hold.
          const from = pressFrom.current
          if (from && Math.hypot(e.clientX - from.x, e.clientY - from.y) > 10) cancelPress()
        }}
        onPointerUp={cancelPress}
        onPointerCancel={cancelPress}
        onContextMenu={(e) => {
          // The phone's own long-press menu, never a mouse's right-click.
          if (touching.current && hasActions && editing === null) e.preventDefault()
        }}
        onClick={() => {
          // The tap that ends a hold has already opened it; do not close it again.
          if (longPressed.current) {
            longPressed.current = false
            return
          }
          // A tap shows the actions on a phone; a second tap puts them away.
          if (hasActions && editing === null) setSelected((s) => !s)
        }}
        className={`rounded-2xl border px-3 py-2 text-[13.5px] leading-relaxed wrap-anywhere ${
          /*
           * On a touch screen: pointer cursor, because iOS Safari sends no click to
           * a plain div without one; no text selection or callout, because those are
           * what a hold was doing instead. Never while editing - iOS can refuse to
           * type into a field whose ancestor is unselectable.
           */
          hasActions && editing === null
            ? '[@media(pointer:coarse)]:cursor-pointer [@media(pointer:coarse)]:select-none [-webkit-touch-callout:none]'
            : ''
        } ${
          m.isMine
            ? 'rounded-br-sm border-accent-soft bg-accent-soft'
            : m.mentionsMe
              ? 'rounded-bl-sm border-accent bg-ground'
              : 'rounded-bl-sm border-line bg-ground'
        }`}
      >
        {m.isHidden && !reviewerSees ? (
          <span className="italic text-ink-3">
            {text(content, 'messages.hidden', 'This message was removed by the studio.')}
          </span>
        ) : editing !== null ? (
          <form onSubmit={saveEdit} className="flex flex-col gap-2">
            <textarea
              value={editing}
              onChange={(e) => setEditing(e.target.value)}
              rows={3}
              maxLength={4000}
              aria-label={text(content, 'messages.edit', 'Edit')}
              className="min-h-[44px] w-full resize-y rounded-lg border border-line bg-surface px-3 py-2 text-[14px]"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={edit.isPending || (editing ?? '').trim().length === 0}
                className="min-h-[44px] rounded-lg bg-accent px-3 text-[13px] font-semibold text-on-accent disabled:opacity-50"
              >
                {text(content, 'messages.edit.save', 'Save')}
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="min-h-[44px] rounded-lg border border-line bg-surface px-3 text-[13px] font-semibold text-ink-2"
              >
                {text(content, 'messages.edit.cancel', 'Cancel')}
              </button>
            </div>
            {edit.isError && (
              <p className="text-[12px] text-crit" role="alert">
                {edit.error.message}
              </p>
            )}
          </form>
        ) : m.subject ? (
          <Card message={m} content={content} readOnly={readOnly} />
        ) : (
          <>
            {/*
              A picture with no caption is a whole message (D-751), so the
              words are rendered only when there are any - an empty span would
              leave a blank line above the grid.
            */}
            {m.body && <span className="whitespace-pre-wrap">{m.body}</span>}
            <PictureGrid
              conversationId={conversationId}
              pictures={m.attachments ?? []}
              urlFor={pictureUrl}
              mayOpen={() => !longPressed.current}
            />
          </>
        )}

        {reviewerSees && (
          <p className="mt-1 text-[12px] italic text-ink-3">
            {fill(content, 'messages.hidden.reviewer', { reason: m.hiddenReason ?? '' }) ||
              `Hidden by the studio: ${m.hiddenReason}`}
          </p>
        )}
      </div>
      {hasActions && editing === null && (
        <div
          role="toolbar"
          aria-label="Message actions"
          className={`absolute -top-4 z-10 flex items-center gap-0.5 rounded-full border border-line bg-surface px-1 py-0.5 shadow-sm transition-opacity ${
            m.isMine ? 'right-2' : 'left-2'
          } ${
            toolbarShown
              ? 'opacity-100'
              : 'pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100'
          }`}
        >
          {reactable && (
            <button
              type="button"
              onClick={() => setPicking((p) => !p)}
              aria-expanded={picking}
              aria-label={text(content, 'messages.react', 'React')}
              title={text(content, 'messages.react', 'React')}
              className="flex h-6 w-6 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink"
            >
              <SmilePlus size={15} aria-hidden="true" />
            </button>
          )}
          {copyable && (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(m.body ?? '').then(() => {
                  setCopied(true)
                  window.setTimeout(() => setCopied(false), 1500)
                })
              }}
              aria-label={copied ? 'Copied' : 'Copy'}
              title={copied ? 'Copied' : 'Copy'}
              className="flex h-6 w-6 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink"
            >
              {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
            </button>
          )}
          {editable && editing === null && (
            <button
              type="button"
              onClick={() => {
                setMore(false)
                setSelected(false)
                setEditing(m.body ?? '')
              }}
              aria-label={text(content, 'messages.edit', 'Edit')}
              title={text(content, 'messages.edit', 'Edit')}
              className="flex h-6 w-6 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink"
            >
              <Pencil size={14} aria-hidden="true" />
            </button>
          )}
          {((hideable && hiding === null) || (pinnable && !isPinned)) && (
            // Not a positioning box: the menu anchors to the whole toolbar, so it
            // opens beside it rather than over the other two buttons.
            <div
              ref={moreRef}
              onBlur={(e) => {
                // Tabbing out closes it too, or it waits open behind her.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setMore(false)
              }}
            >
              <button
                type="button"
                ref={moreButtonRef}
                onClick={() => setMore((o) => !o)}
                aria-expanded={more}
                aria-label="More actions"
                title="More actions"
                className="flex h-6 w-6 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink"
              >
                <MoreHorizontal size={15} aria-hidden="true" />
              </button>
              {more && (
                <div
                  className={`absolute top-0 z-20 flex min-w-[9rem] flex-col rounded-lg border border-line bg-surface py-1 shadow-md ${
                    m.isMine ? 'right-full mr-1' : 'left-full ml-1'
                  }`}
                >
                  {/*
                    <b>A literal, and its neighbours are content blocks</b> — D-678
                    rather than an oversight: inside the signed-in application the
                    words are ours.

                    <b>Pin is not offered on a system line or a hidden one.</b> A
                    hidden message would put a blank in the banner; a card is refused
                    because its meaning is the live state beside it (D-630), so a
                    pinned card would read as open long after it was answered.
                    `pinnable` is the shape the server checks.
                  */}
                  {pinnable && !isPinned && (
                    <button
                      type="button"
                      onClick={() => {
                        setMore(false)
                        setSelected(false)
                        onPin?.(true)
                      }}
                      className="min-h-[36px] px-3 text-left text-[13px] font-semibold text-ink hover:bg-surface-2"
                    >
                      Pin
                    </button>
                  )}
                  {hideable && hiding === null && (
                    <button
                      type="button"
                      onClick={() => {
                        setMore(false)
                        setSelected(false)
                        setHiding('')
                      }}
                      className="min-h-[36px] px-3 text-left text-[13px] font-semibold text-crit hover:bg-surface-2"
                    >
                      {text(content, 'messages.hide', 'Hide')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>

      {(shown.length > 0 || picking || react.isError) && (
        <div className="flex flex-wrap items-center gap-1 px-1">
          {shown.map((r) => (
            <button
              key={r.emoji}
              type="button"
              aria-pressed={r.mine}
              disabled={!reactable || react.isPending}
              onClick={() => react.mutate({ emoji: r.emoji, on: !r.mine })}
              className={`inline-flex min-h-[24px] items-center gap-1 rounded-full border px-2 text-[12px] ${
                r.mine ? 'border-accent bg-accent-soft font-semibold' : 'border-line bg-surface'
              }`}
            >
              <span aria-hidden="true">{glyphOf(r.emoji)}</span>
              <span className="sr-only">{nameOf(r.emoji)}</span>
              {r.count}
            </button>
          ))}

          {picking && reactable && (
            <div role="group" aria-label={text(content, 'messages.react', 'React')} className="flex flex-wrap gap-1">
              {offered.map((o) => {
                const mine = m.reactions.some((r) => r.emoji === o.key && r.mine)
                return (
                  <button
                    key={o.key}
                    type="button"
                    aria-pressed={mine}
                    disabled={react.isPending}
                    onClick={() => react.mutate({ emoji: o.key, on: !mine })}
                    className={`flex h-9 w-9 items-center justify-center rounded-full border text-[18px] ${
                      mine ? 'border-accent bg-accent-soft' : 'border-line bg-surface'
                    }`}
                  >
                    <span aria-hidden="true">{o.glyph}</span>
                    <span className="sr-only">{nameOf(o.key)}</span>
                  </button>
                )
              })}
            </div>
          )}

          {react.isError && (
            <span className="text-[12px] text-crit" role="alert">
              {text(content, 'messages.reaction.failed', 'That reaction could not be saved. Try again.')}
            </span>
          )}
        </div>
      )}

      <span className="flex flex-wrap items-center gap-x-1 px-1 text-[11px] text-ink-3">
        {m.isMine ? '' : `${m.authorName}${DOT}`}
        {whenLabel(m.sentAt, now)}
        {/* Only where the history can open: a hidden message's is the reviewer's alone. */}
        {m.isEdited && (!m.isHidden || reviewerSees) && (
          <>
            {DOT}
            {readOnly ? (
              <span>{text(content, 'messages.edited', 'Edited')}</span>
            ) : (
              <button
                type="button"
                onClick={() => setShowHistory((s) => !s)}
                aria-expanded={showHistory}
                className="min-h-[24px] min-w-[24px] px-1 underline decoration-dotted underline-offset-2"
              >
                {text(content, 'messages.edited', 'Edited')}
              </button>
            )}
          </>
        )}
        {m.mentionsMe && (
          <>
            {DOT}
            <span className="font-semibold text-accent-2">{text(content, 'messages.mention.you', 'Mentioned you')}</span>
          </>
        )}
        {/*
          <b>The row says it is the pinned one; the banner is where it comes
          off.</b> An <i>Unpin</i> here as well would put two controls with the
          same name on one screen — ambiguous to a screen reader, and to anybody
          scanning. One place to undo something is enough.
        */}
        {isPinned && !m.isHidden && (
          <>
            {DOT}
            <span className="font-semibold text-accent-2">At the top</span>
          </>
        )}
        {/*
          <b>Whether the message landed</b> (D-693, item 21).

          Only ever on my own message, and the server decides that — a receipt
          is a hole in other people's privacy, so it opens in one place.

          <b>Two shapes for two questions.</b> One-to-one it is *did she read
          it*, and a plain *Seen* answers that; a group is *how many of them
          have*, which needs the number and the total. Rendering "1 of 1" to a
          parent would be the right fact in the wrong voice.

          <b>Keyed on the kind of conversation, never on the total.</b> Reading
          `of === 1` as "one-to-one" was wrong on real data: a class with one
          family makes a group whose audience is one person, and it then
          rendered as a direct message and went silent at zero — the one number
          its creator most wanted.

          <b>Silent until somebody has read it</b> in a one-to-one. "0 of 1"
          under every message she sends is a running tally of somebody else not
          answering, which is not what she asked for and reads as nagging; a
          group shows zero because the number is the point and it is going
          somewhere.
        */}
        {m.seen && !isGroup && m.seen.count > 0 && (
          <>
            {DOT}
            <span>Seen</span>
          </>
        )}
        {m.seen && isGroup && (
          <>
            {DOT}
            <span>
              {m.seen.count} of {m.seen.of} have seen this
            </span>
          </>
        )}
      </span>

      {showHistory && (
        <div className="w-full max-w-[85%] rounded-xl border border-line bg-surface-2 px-3 py-2 text-[12px] text-ink-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">{text(content, 'messages.history.heading', 'Every version of this message')}</h3>
            <button type="button" onClick={() => setShowHistory(false)} className="min-h-[24px] min-w-[24px] px-1 font-semibold text-accent-2">
              {text(content, 'messages.history.close', 'Close')}
            </button>
          </div>
          {history.isPending && (
            <p role="status">{text(content, 'messages.loading', 'Loading…')}</p>
          )}
          {history.isError && (
            <p className="text-crit" role="alert">
              {text(content, 'messages.history.failed', 'The history could not be loaded. Try again in a moment.')}
            </p>
          )}
          {history.data && (
            <ol className="mt-1 flex flex-col gap-2">
              {history.data.entries.map((entry, i) => (
                <li key={entry.sequence} className="flex flex-col gap-0.5">
                  <span className="whitespace-pre-wrap text-ink wrap-anywhere">{entry.body}</span>
                  <span className="text-[11px] text-ink-3">
                    {i === 0 ? `${text(content, 'messages.history.original', 'Original')}${DOT}` : ''}
                    {entry.byName}
                    {DOT}
                    {whenLabel(entry.writtenAt, now)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {hiding !== null && (
        <form
          onSubmit={confirmHide}
          className="flex w-full max-w-[85%] flex-col gap-2 rounded-xl border border-line bg-surface-2 px-3 py-2"
        >
          <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
            {text(content, 'messages.hide.reason', 'Why is this being hidden?')}
            <textarea
              value={hiding}
              onChange={(e) => {
                setHiding(e.target.value)
                setHideRefusal(null)
              }}
              rows={2}
              maxLength={500}
              className="min-h-[44px] w-full resize-y rounded-lg border border-line bg-surface px-3 py-2 text-[14px] font-normal"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={hide.isPending}
              className="min-h-[44px] rounded-lg bg-accent px-3 text-[13px] font-semibold text-on-accent disabled:opacity-50"
            >
              {text(content, 'messages.hide.confirm', 'Hide this message')}
            </button>
            <button
              type="button"
              onClick={() => {
                setHiding(null)
                setHideRefusal(null)
              }}
              className="min-h-[44px] rounded-lg border border-line bg-surface px-3 text-[13px] font-semibold text-ink-2"
            >
              {text(content, 'messages.hide.cancel', 'Cancel')}
            </button>
          </div>
          {(hideRefusal || hide.isError) && (
            <p className="text-[12px] text-crit" role="alert">
              {hideRefusal ?? hide.error?.message}
            </p>
          )}
        </form>
      )}
    </li>
  )
}

/**
 * Keyed on the conversation, so moving from one to another through the
 * household line starts the screen over. An earlier version kept the draft,
 * the read marker and the last error across the change - React reuses the
 * instance for one route pattern - so a message typed to one guardian went to
 * the other. Found by an audit, on the feature's own headline affordance.
 */
export default function ConversationView() {
  const { id = '' } = useParams<{ id: string }>()

  return <Conversation key={id} id={id} />
}

/**
 * The way back to the list, on the one width where the list is not on screen
 * (D-702).
 *
 * <b>`asked`</b>: *"having to click back to all threads"* was the complaint
 * that produced the two-pane layout. From `md` up the list is in the column to
 * the left with this conversation's row highlighted in it, so a control whose
 * whole job is *go and find the list* is pointing at something already visible
 * — and one that does nothing you can see is worse than none.
 *
 * <b>Hidden rather than removed.</b> At a phone width the detail pane is the
 * whole screen and this is the only way back, so it is not furniture there; it
 * is the navigation.
 */
function BackLink({ content }: { content: Content }) {
  return (
    <Link
      to="/messages"
      className="inline-flex min-h-[44px] items-center gap-2 text-[13px] font-semibold text-ink-2 md:hidden"
    >
      <ArrowLeft size={16} aria-hidden="true" />
      {text(content, 'messages.back', 'All messages')}
    </Link>
  )
}
